import reveal from '../reveal';

// Matches $bp-sm in src/scss/abstracts/_tokens.scss
const MOBILE_BREAKPOINT = 640;

interface ParsedValue {
	prefix: string;
	suffix: string;
	target: number;
	decimals: number;
	grouped: boolean;
}

function parseValue(text: string): ParsedValue | null {
	const match = text.match(/^(\D*)([\d,]*\d(?:\.\d+)?)(\D*)$/);

	if (!match) {
		return null;
	}

	const [, prefix, numberPart, suffix] = match;
	const grouped = numberPart.includes(',');
	const clean = numberPart.replace(/,/g, '');
	const target = parseFloat(clean);

	if (Number.isNaN(target)) {
		return null;
	}

	const decimalMatch = clean.match(/\.(\d+)$/);
	const decimals = decimalMatch ? decimalMatch[1].length : 0;

	return { prefix, suffix, target, decimals, grouped };
}

function format(value: number, decimals: number, grouped: boolean): string {
	const fixed = value.toFixed(decimals);

	if (!grouped) {
		return fixed;
	}

	const [intPart, decPart] = fixed.split('.');
	const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

	return decPart ? `${withCommas}.${decPart}` : withCommas;
}

function animateValue(el: HTMLElement, parsed: ParsedValue): void {
	const duration = 1400;
	const ease = (t: number): number => 1 - (1 - t) ** 3;
	const start = performance.now();

	const step = (now: number): void => {
		const t = Math.min((now - start) / duration, 1);
		const value = parsed.target * ease(t);

		el.textContent = `${parsed.prefix}${format(value, parsed.decimals, parsed.grouped)}${parsed.suffix}`;

		if (t < 1) {
			window.requestAnimationFrame(step);
		}
	};

	window.requestAnimationFrame(step);
}

function initEntrance(items: HTMLElement[]): void {
	reveal(items, (item) => {
		const valueEl = item.querySelector<HTMLElement>('.armada-stats__value');
		const parsed = valueEl ? parseValue(valueEl.textContent?.trim() ?? '') : null;

		if (valueEl && parsed) {
			animateValue(valueEl, parsed);
		}
	});
}

function initHoverFill(cards: HTMLElement[]): void {
	if (!cards.length) {
		return;
	}

	const phone = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
	let queued = false;

	const paint = (): void => {
		const middle = window.innerHeight / 2;

		cards.forEach((card) => {
			const box = card.getBoundingClientRect();
			card.classList.toggle('is-filled', box.top <= middle && box.bottom >= middle);
		});
	};

	const request = (): void => {
		if (queued) {
			return;
		}

		queued = true;
		window.requestAnimationFrame(() => {
			queued = false;
			paint();
		});
	};

	// Desktop already gets the fill from :hover in CSS
	const sync = (): void => {
		if (phone.matches) {
			window.addEventListener('scroll', request, { passive: true });
			window.addEventListener('resize', request);
			request();
			return;
		}

		window.removeEventListener('scroll', request);
		window.removeEventListener('resize', request);
		cards.forEach((card) => card.classList.remove('is-filled'));
	};

	phone.addEventListener('change', sync);
	sync();
}

export default function initStats(): void {
	const cards: HTMLElement[] = [];

	document.querySelectorAll<HTMLElement>('.armada-stats').forEach((block) => {
		const items = Array.from(block.querySelectorAll<HTMLElement>('.armada-stats__item'));
		cards.push(...items);
		initEntrance(items);
	});

	initHoverFill(cards);
}
